import React, { createContext, useContext, useReducer, useMemo, useCallback } from 'react';

// Create separate contexts for state and actions
const SidebarStateContext = createContext();
const SidebarActionsContext = createContext();

// Initial state
const initialState = {
  isOpen: false,
  title: '',
  content: null,
  className: 'right-sidebar-md' // Default class
};

// Reducer function
const sidebarReducer = (state, action) => {
  switch (action.type) {
    case 'OPEN_SIDEBAR':
      return {
        ...state,
        isOpen: true,
        title: action.payload.title,
        content: action.payload.content,
        className: action.payload.className || state.className
      };
    case 'CLOSE_SIDEBAR':
      return {
        ...state,
        isOpen: false,
        title: '',
        content: null,
        className: state.className
      };
    case 'SET_CLASS':
      return {
        ...state,
        className: action.payload
      };
    default:
      return state;
  }
};

// Provider component
export const SidebarProvider = ({ children }) => {
  const [state, dispatch] = useReducer(sidebarReducer, initialState);

  // Memoize actions
  const actions = useMemo(() => ({
    openSidebar: (payload) => dispatch({ type: 'OPEN_SIDEBAR', payload }),
    closeSidebar: () => dispatch({ type: 'CLOSE_SIDEBAR' }),
    setClass: (className) => dispatch({ type: 'SET_CLASS', payload: className }),
    toggleSidebar: (payload) => {
      if (state.isOpen) {
        dispatch({ type: 'CLOSE_SIDEBAR' });
      } else {
        dispatch({ type: 'OPEN_SIDEBAR', payload });
      }
    }
  }), [state.isOpen]);

  // Memoize state
  const sidebarState = useMemo(() => state, [state]);

  return (
    <SidebarStateContext.Provider value={sidebarState}>
      <SidebarActionsContext.Provider value={actions}>
        {children}
      </SidebarActionsContext.Provider>
    </SidebarStateContext.Provider>
  );
};

// Custom hooks
export const useSidebarState = () => {
  const context = useContext(SidebarStateContext);
  if (context === undefined) {
    throw new Error('useSidebarState must be used within a SidebarProvider');
  }
  return context;
};

export const useSidebarActions = () => {
  const context = useContext(SidebarActionsContext);
  if (context === undefined) {
    throw new Error('useSidebarActions must be used within a SidebarProvider');
  }
  return context;
};

// Combined hook for backward compatibility
export const useSidebar = () => {
  const state = useSidebarState();
  const actions = useSidebarActions();
  return {
    ...state,
    ...actions
  };
};

export default SidebarProvider;